// TypeCatalogService.js
//
// Platform catalog entries live under `_nodetypes/{typeKey}/_catalog/{catalogId}`.
// See docs/appdeck-platform-catalog-v1-spec.md.

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes } from 'firebase/storage';
import { collections, db, functions, httpsCallable } from 'firebaseConfig';
import { generateKey } from 'utilities/Keys';
import { isBlobUrl } from 'utilities/Images';
import { getCatalogSchemaFields } from 'utilities/PlatformCatalog';
import { buildNodeFieldPatch, readNodeFieldRawValue } from 'utilities/NodeFieldData';

const NODETYPES_COLLECTION = collections.nodetypes;
const TYPE_CATALOG_SUBCOLLECTION = collections.nodeTypeCatalog || '_catalog';

function normalizeString(value) {
  return String(value || '').trim();
}

function typeCatalogCollectionRef(typeKey) {
  return collection(
    db,
    NODETYPES_COLLECTION,
    normalizeString(typeKey),
    TYPE_CATALOG_SUBCOLLECTION,
  );
}

function typeCatalogDocRef(typeKey, catalogId) {
  return doc(
    db,
    NODETYPES_COLLECTION,
    normalizeString(typeKey),
    TYPE_CATALOG_SUBCOLLECTION,
    normalizeString(catalogId),
  );
}

/**
 * @param {string} typeKey
 * @param {object} data
 * @param {string} [docId]
 * @returns {object}
 */
export function normalizeCatalogEntry(typeKey, data = {}, docId = '') {
  const cleanedTypeKey = normalizeString(typeKey);
  const key = normalizeString(data?.key) || normalizeString(docId);
  return {
    ...(data || {}),
    id: key || normalizeString(docId),
    key: key || normalizeString(docId),
    typeKey: cleanedTypeKey || normalizeString(data?.typeKey),
    title: normalizeString(data?.title),
    status: normalizeString(data?.status) || 'active',
    coverUrl: normalizeString(data?.coverUrl) || null,
    coverStoragePath: normalizeString(data?.coverStoragePath) || normalizeString(data?.coverUrl) || null,
  };
}

/**
 * Upload a catalog cover image to Storage under nodetypes/{typeKey}/_catalog/{id}/…
 *
 * @param {string} typeKey
 * @param {string} catalogId
 * @param {Blob} file
 * @returns {Promise<string>} Storage path
 */
export async function uploadTypeCatalogCover(typeKey, catalogId, file) {
  const cleanedTypeKey = normalizeString(typeKey);
  const cleanedCatalogId = normalizeString(catalogId);
  if (!cleanedTypeKey || !cleanedCatalogId) {
    throw new Error('typeKey and catalogId are required');
  }
  if (!(file instanceof Blob)) {
    throw new Error('An image file is required');
  }

  const ext = (() => {
    const name = String(file.name || '').trim();
    const fromName = name.includes('.') ? name.split('.').pop() : '';
    if (fromName) return fromName.toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
    const mime = String(file.type || '').trim().toLowerCase();
    if (mime === 'image/png') return 'png';
    if (mime === 'image/webp') return 'webp';
    if (mime === 'image/gif') return 'gif';
    return 'jpg';
  })();

  const storagePath = `nodetypes/${cleanedTypeKey}/_catalog/${cleanedCatalogId}/cover.${ext}`;
  const storage = getStorage();
  const storageRef = ref(storage, storagePath);
  const metadata = file.type ? { contentType: file.type } : undefined;
  await uploadBytes(storageRef, file, metadata);
  return storagePath;
}

/**
 * Subscribe to a single catalog entry.
 *
 * @param {string} typeKey
 * @param {string} catalogId
 * @param {function(object|null): void} callback
 * @returns {function(): void}
 */
export function subscribeToTypeCatalogEntry(typeKey, catalogId, callback) {
  const cleanedTypeKey = normalizeString(typeKey);
  const cleanedCatalogId = normalizeString(catalogId);
  if (!cleanedTypeKey || !cleanedCatalogId || typeof callback !== 'function') {
    if (typeof callback === 'function') callback(null);
    return () => {};
  }

  return onSnapshot(
    typeCatalogDocRef(cleanedTypeKey, cleanedCatalogId),
    (snap) => {
      if (!snap.exists()) {
        callback(null);
        return;
      }
      const entry = normalizeCatalogEntry(cleanedTypeKey, snap.data() || {}, snap.id);
      if ((entry.status || 'active') === 'deleted') {
        callback(null);
        return;
      }
      callback(entry);
    },
    (error) => {
      console.error('[TypeCatalogService] subscribeToTypeCatalogEntry error:', error);
      callback(null);
    },
  );
}

/**
 * Subscribe to catalog entries for a system type.
 *
 * @param {string} typeKey
 * @param {function(object[]): void} callback
 * @returns {function(): void}
 */
export function subscribeToTypeCatalog(typeKey, callback) {
  const cleanedTypeKey = normalizeString(typeKey);
  if (!cleanedTypeKey || typeof callback !== 'function') {
    if (typeof callback === 'function') callback([]);
    return () => {};
  }

  return onSnapshot(
    typeCatalogCollectionRef(cleanedTypeKey),
    (snapshot) => {
      const rows = snapshot.docs
        .map((docSnap) => normalizeCatalogEntry(cleanedTypeKey, docSnap.data() || {}, docSnap.id))
        .filter((row) => (row.status || 'active') !== 'deleted')
        .sort((left, right) => (
          String(left.title || '').localeCompare(String(right.title || ''), undefined, {
            sensitivity: 'base',
          })
        ));
      callback(rows);
    },
    (error) => {
      console.error('[TypeCatalogService] subscribeToTypeCatalog error:', error);
      callback([]);
    },
  );
}

/**
 * One-shot list of catalog entries.
 *
 * @param {string} typeKey
 * @returns {Promise<object[]>}
 */
export async function listTypeCatalog(typeKey) {
  const cleanedTypeKey = normalizeString(typeKey);
  if (!cleanedTypeKey) return [];

  const snapshot = await getDocs(typeCatalogCollectionRef(cleanedTypeKey));
  return snapshot.docs
    .map((docSnap) => normalizeCatalogEntry(cleanedTypeKey, docSnap.data() || {}, docSnap.id))
    .filter((row) => (row.status || 'active') !== 'deleted')
    .sort((left, right) => (
      String(left.title || '').localeCompare(String(right.title || ''), undefined, {
        sensitivity: 'base',
      })
    ));
}

/**
 * @param {string} typeKey
 * @param {string} catalogId
 * @returns {Promise<object|null>}
 */
export async function getTypeCatalogEntry(typeKey, catalogId) {
  const cleanedTypeKey = normalizeString(typeKey);
  const cleanedCatalogId = normalizeString(catalogId);
  if (!cleanedTypeKey || !cleanedCatalogId) return null;

  const snap = await getDoc(typeCatalogDocRef(cleanedTypeKey, cleanedCatalogId));
  if (!snap.exists()) return null;
  return normalizeCatalogEntry(cleanedTypeKey, snap.data() || {}, snap.id);
}

/**
 * Build a NodeForm-shaped draft from a catalog entry + type schema.
 *
 * @param {string} typeKey
 * @param {object|null} entry
 * @param {object[]} schemaFields
 * @returns {object}
 */
export function buildCatalogDraftNode(typeKey, entry = null, schemaFields = []) {
  const cleanedTypeKey = normalizeString(typeKey);
  const dataKey = `data_${cleanedTypeKey.toLowerCase()}`;
  const catalogId = normalizeString(entry?.key || entry?.id) || generateKey();
  const coverPath = normalizeString(entry?.coverStoragePath) || normalizeString(entry?.coverUrl);
  const draft = {
    key: catalogId,
    type: cleanedTypeKey,
    title: normalizeString(entry?.title),
    coverUrl: coverPath || null,
    coverStoragePath: coverPath || null,
    [dataKey]: {},
  };

  const bagSource = entry?.[dataKey] || entry?.data || {};
  getCatalogSchemaFields(schemaFields).forEach((field) => {
    const name = normalizeString(field?.name);
    if (!name) return;
    // Cover is a root node field (coverUrl), not a data_* bag value.
    if (String(field?.type || '').trim().toLowerCase() === 'cover') return;
    let value = readNodeFieldRawValue(entry, field, cleanedTypeKey);
    if (value === undefined && bagSource && typeof bagSource === 'object') {
      value = bagSource[name];
    }
    if (value === undefined) return;
    const patch = buildNodeFieldPatch(draft, field, cleanedTypeKey, value);
    if (patch) Object.assign(draft, patch);
  });

  return draft;
}

/**
 * Persist a catalog entry from a NodeForm-shaped draft.
 *
 * @param {string} typeKey
 * @param {object} draftNode
 * @param {object[]} schemaFields
 * @param {object} [options]
 * @param {string} [options.seedSource]
 * @returns {Promise<object>}
 */
export async function saveTypeCatalogEntry(typeKey, draftNode, schemaFields = [], options = {}) {
  const cleanedTypeKey = normalizeString(typeKey);
  if (!cleanedTypeKey) throw new Error('typeKey is required');

  const catalogId = normalizeString(draftNode?.key) || generateKey();
  const dataKey = `data_${cleanedTypeKey.toLowerCase()}`;
  const data = {};

  getCatalogSchemaFields(schemaFields).forEach((field) => {
    const name = normalizeString(field?.name);
    if (!name) return;
    if (String(field?.type || '').trim().toLowerCase() === 'cover') return;
    const value = readNodeFieldRawValue(draftNode, field, cleanedTypeKey);
    if (value !== undefined) data[name] = value;
  });

  const now = Timestamp.now();
  const existing = await getTypeCatalogEntry(cleanedTypeKey, catalogId);

  let coverUrl = normalizeString(existing?.coverUrl) || null;
  let coverStoragePath = normalizeString(existing?.coverStoragePath) || coverUrl;

  if (draftNode?._coverRemoved === true) {
    coverUrl = null;
    coverStoragePath = null;
  } else if (draftNode?._pendingCoverBlob instanceof Blob) {
    const storagePath = await uploadTypeCatalogCover(
      cleanedTypeKey,
      catalogId,
      draftNode._pendingCoverBlob,
    );
    coverUrl = storagePath;
    coverStoragePath = storagePath;
  } else {
    const draftCover = normalizeString(draftNode?.coverStoragePath)
      || normalizeString(draftNode?.coverUrl);
    if (draftCover && !isBlobUrl(draftCover)) {
      coverUrl = draftCover;
      coverStoragePath = draftCover;
    }
  }

  const payload = {
    key: catalogId,
    typeKey: cleanedTypeKey,
    title: normalizeString(draftNode?.title),
    status: 'active',
    data,
    [dataKey]: data,
    coverUrl,
    coverStoragePath,
    dateModified: now,
    dateCreated: existing?.dateCreated || now,
  };

  if (options.seedSource) {
    payload.seedSource = normalizeString(options.seedSource);
  } else if (existing?.seedSource) {
    payload.seedSource = existing.seedSource;
  }

  await setDoc(typeCatalogDocRef(cleanedTypeKey, catalogId), payload, { merge: true });
  return normalizeCatalogEntry(cleanedTypeKey, payload, catalogId);
}

/**
 * Delete a catalog entry (client-only, no detach). Prefer deleteTypeCatalogEntryWithDetach.
 *
 * @param {string} typeKey
 * @param {string} catalogId
 * @returns {Promise<void>}
 */
export async function deleteTypeCatalogEntryDoc(typeKey, catalogId) {
  const cleanedTypeKey = normalizeString(typeKey);
  const cleanedCatalogId = normalizeString(catalogId);
  if (!cleanedTypeKey || !cleanedCatalogId) return;
  await deleteDoc(typeCatalogDocRef(cleanedTypeKey, cleanedCatalogId));
}

/**
 * Delete catalog entry and detach/materialize adopted workspace pointers (Cloud Function).
 *
 * @param {string} typeKey
 * @param {string} catalogId
 * @returns {Promise<{ detachedCount: number }>}
 */
export async function deleteTypeCatalogEntryWithDetach(typeKey, catalogId) {
  const cleanedTypeKey = normalizeString(typeKey);
  const cleanedCatalogId = normalizeString(catalogId);
  if (!cleanedTypeKey || !cleanedCatalogId) {
    throw new Error('typeKey and catalogId are required');
  }

  const call = httpsCallable(functions, 'deleteTypeCatalogEntry');
  const result = await call({ typeKey: cleanedTypeKey, catalogId: cleanedCatalogId });
  return result?.data || { detachedCount: 0 };
}
